import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { useGreenhouses } from "@/features/farms/hooks/useGreenhouses";
import { useAuthStore } from "@/features/auth/stores/authStore";


type FilterType = "all" | "my" | "pending-approvals" | "pending-issue";

interface RequisitionFiltersProps {
  filterType: FilterType;
  onFilterTypeChange: (type: FilterType) => void;
  startDate: string;
  onStartDateChange: (date: string) => void;
  endDate: string;
  onEndDateChange: (date: string) => void;
  greenhouseId: string;
  onGreenhouseIdChange: (id: string) => void;
}

export const RequisitionFilters = ({
  filterType,
  onFilterTypeChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  greenhouseId,
  onGreenhouseIdChange,
}: RequisitionFiltersProps) => {
  const { data: greenhouses = [] } = useGreenhouses();
  const { hasPermission } = useAuthStore();

  const ALL_FILTER_OPTIONS: { value: FilterType; label: string; requiredPermission?: string }[] = [
    { value: "all", label: "All Requisitions" },
    { value: "my", label: "My Requisitions" },
    { value: "pending-approvals", label: "Pending Approvals", requiredPermission: "requisitions.approve" },
    { value: "pending-issue", label: "Pending Issue", requiredPermission: "requisitions.issue" },
  ];

  const FILTER_OPTIONS = ALL_FILTER_OPTIONS.filter(
    (opt) => !opt.requiredPermission || hasPermission(opt.requiredPermission)
  );

  return (
    <div className="rounded-lg border p-4 space-y-4">
      {/* Filter Type Buttons */}
      <div>
        <p className="text-sm font-medium mb-2">Filter By Status</p>
        <div className="flex gap-2">
          {FILTER_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant={filterType === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => onFilterTypeChange(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Date Range & Greenhouse Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field className="col-span-2">
          <FieldLabel htmlFor="dateRange">Date Range</FieldLabel>
          <DateRangePicker
            id="dateRange"
            startDate={startDate}
            endDate={endDate}
            onChange={({ startDate: newStartDate, endDate: newEndDate }) => {
              onStartDateChange(newStartDate ?? "")
              onEndDateChange(newEndDate ?? "")
            }}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="greenhouseId">Greenhouse</FieldLabel>
          <select
            id="greenhouseId"
            value={greenhouseId}
            onChange={(e) => onGreenhouseIdChange(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">All Greenhouses</option>
            {greenhouses.map((gh) => (
              <option key={gh.id} value={gh.id}>
                {gh.greenhouseName}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* Clear Filters */}
      {(startDate || endDate || greenhouseId) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onStartDateChange("");
            onEndDateChange("");
            onGreenhouseIdChange("");
          }}
        >
          Clear Date & Greenhouse Filters
        </Button>
      )}
    </div>
  );
};